import { useContext, useRoute } from "@nuxtjs/composition-api";
import { useAdminUserSwitcher } from "~/components/_refactored/admin";
import { resolvePageName } from "~/plugins/trackers";
import { resolveDeviceType } from "~/composables/tracking/useTrackPersonTenderContactClick";
import TRACK_FRONTEND_EVENT from "~/graphql/mutations/trackFrontendEvent.graphql";

export const FRONTEND_EVENT_TYPE = {
  PHONE_CLICK: "phone_click",
};

export const FRONTEND_EVENT_PLACEMENT = {
  PERSON_CARD: "person_card",
  STICKY_PANEL: "sticky_panel",
};

/**
 * Records a frontend event (phone-button clicks first) to our own DB via the
 * public trackFrontendEvent mutation. First-party counterpart of the GA/PostHog
 * events: same-origin, so ad-blockers cannot drop it — this is the click count
 * the third-party numbers get compared against.
 *
 * Fire-and-forget: the caller must stay synchronous (tel:/window.open must not
 * be delayed) and no failure may ever become visible to the visitor.
 *
 * anonymousId is the joszaki-uuid cookie, which is also the PostHog
 * distinct_id — that is what makes row-level joins against the PostHog export
 * possible. Nothing is recorded while an admin is impersonating a szaki, so
 * admin activity stays out of the stats (the API cannot tell them apart).
 */
export function useTrackFrontendEvent() {
  const { $mutate, $device, $uuid } = useContext();
  const route = useRoute();
  const adminUserSwitcher = useAdminUserSwitcher();

  function trackFrontendEvent({ eventType, personId, placement, metadata }) {
    if (!process.client) return;

    // The whole body is guarded: a synchronous throw here would propagate into
    // the click handler and could block what runs after it (on the list page
    // window.open comes last, so the profile would never open; on the profile
    // the legacy tracker legs would be skipped). Tracking must never cost the
    // click it rides on.
    try {
      if (adminUserSwitcher.switchedToMate) return;

      const routeName = route.value?.name;

      // Not awaited. skipErrorPageOnAuthError is what stops an expired JWT
      // (UNAUTHENTICATED/FORBIDDEN) from routing the visitor to the error page —
      // showErrorPage: false alone does not cover the auth codes.
      $mutate(
        TRACK_FRONTEND_EVENT,
        {
          eventType,
          personId: personId ?? null,
          // resolvePageName throws on a missing route name, hence the guard.
          // Unmapped routes fall back to the non-localized route name rather
          // than NULL, so the page dimension survives on unmapped pages.
          page: routeName
            ? resolvePageName(routeName) ?? routeName.split("___")[0]
            : null,
          placement: placement ?? null,
          deviceType: resolveDeviceType($device),
          anonymousId: $uuid ?? null,
          metadata: metadata ? JSON.stringify(metadata) : null,
        },
        {
          showErrorPage: false,
          showErrorToast: false,
          skipErrorPageOnAuthError: true,
        }
      ).catch(() => {});
    } catch {
      // Swallowed on purpose — see above.
    }
  }

  return {
    trackFrontendEvent,
  };
}
